import { FC, useState } from "react";
import { Validators } from "../../../util/validators";
import { CustomButton } from "../../shared/CustomButton";
import { AutoInput, TextInput } from "../../shared";
import { useFormState } from "../../../hooks/useFormState";
import { Save, Cancel } from '@mui/icons-material';
import { User } from "../../../types/User";
import { Grid, Typography } from "@mui/material";
import { FormWrapper } from "../../shared/FormWrapper";
import { InputGrid } from "../../shared/InputGrid";
import { AuthProvider } from "../../../enum/authProvider.enum";
import { defaultUser } from "../../../defaultValues/defaultUser";
import { PhoneInput } from "../../shared/PhoneInput";
import { countryListWithCodes } from "../../../util/countryHelper";
import { ImageInput } from "../../shared/ImageInput";
import { appendQueryParam } from "../../../util/helper";
import { config } from "../../../config";
import { UserImageType } from "../../../enum/user.type";

interface Props {
  initialState: User;
  onlyCountrySelectorPopup: boolean;
  loading: boolean;
  onSave: (user: User, images: Record<number, Blob | null>) => void;
  onClose: () => void;
}
export const UserUpdate: FC<Props> = ({ onlyCountrySelectorPopup, loading, onSave, onClose, initialState }) => {

  const { state, changeProperty, changeBulk } = useFormState<User>({
    ...defaultUser,
    ...initialState,
  });
  const [images, setImages] = useState<Record<number, Blob | null>>({});
  
  const imagesObj = state.images;
  const defaultCoverImage = appendQueryParam(imagesObj[UserImageType.BusinessCover], 'updatedAt', state.updatedAt.toString()) ?? config.defaultVehicleImageUrl;

  const isReadyToSubmit = () => {
    if (
      Validators.text(state.name) &&
      Validators.country(state.country)
    ) {
      return true;
    }
    return false;
  };

  const handleSignUp = () => {
    const obj = { ...state };
    onSave(obj, images);
  };

  const onImageChange = (img: Blob | null, index: number) => {
    setImages(prev => {
      const next = { ...prev };
      next[index] = img;
      return next;
    });
  };


  return (
    <FormWrapper>
      <Grid container >

        <Typography variant="h4">Account Settings</Typography>

        <InputGrid minWidth={12}>
          <TextInput
            value={state.name}
            name="name"
            label="Enter Your Name"
            error={!Validators.text(state.name)}
            onChange={changeProperty}
            disabled={loading}
          />
        </InputGrid>

        <InputGrid minWidth={12}>
          <AutoInput
            value={state.country}
            name="country"
            error={!Validators.country(state.country)}
            label="Select Country"
            options={countryListWithCodes()}
            disabled={loading}
            onChange={changeProperty}
          />
        </InputGrid>

        <InputGrid minWidth={12}>
          <TextInput
            value={state.shopName ?? ''}
            name="shopName"
            label="Shop Name"
            onChange={changeProperty}
            disabled={loading}
          />
        </InputGrid>

        <InputGrid minWidth={12}>
          <TextInput
            value={state.shopAddress ?? ''}
            name="shopAddress"
            label="Shop Address"
            onChange={changeProperty}
            disabled={loading}
          />
        </InputGrid>

        <InputGrid minWidth={12}>
          <TextInput
            value={state.shopPhone ?? ''}
            name="shopPhone"
            label="Shop Phone"
            onChange={changeProperty}
            disabled={loading}
          />
        </InputGrid>

        <InputGrid minWidth={12}>
          <ImageInput
            entity={state}
            label="Profile Image"
            index={UserImageType.Profile}
            disabled={loading}
            defaultImageUrl={imagesObj[UserImageType.Profile] ?? defaultCoverImage}
            onImageSelected={onImageChange}
          />
        </InputGrid>

        <InputGrid minWidth={12}>
          <ImageInput
            entity={state}
            label="Business Page Cover Image"
            index={UserImageType.BusinessCover}
            disabled={loading}
            defaultImageUrl={imagesObj[UserImageType.BusinessCover] ?? defaultCoverImage}
            onImageSelected={onImageChange}
          />
        </InputGrid>

        {!onlyCountrySelectorPopup && <>
          <InputGrid minWidth={6}>
            <TextInput
              value={`99000${state.id}`}
              name="id"
              label="User Id"
              onChange={changeProperty}
              disabled
            />
          </InputGrid>

          <InputGrid minWidth={12}>
            <PhoneInput
              country={state.country}
              value={state.identifier}
              name="identifier"
              label="Identifier"
              error={!Validators.validatePhone(state.identifier, state.country)}
              onChange={changeProperty}
              disabled
            />
          </InputGrid>

          <InputGrid minWidth={6}>
            <TextInput
              value={AuthProvider[state.authProvider]}
              name="authProvider"
              label="Auth Provider"
              onChange={changeProperty}
              disabled
            />
          </InputGrid>

          <InputGrid minWidth={6}>
            <TextInput
              value={state.phone ?? 'N/A'}
              name="phone"
              label="Phone Number"
              onChange={changeProperty}
              disabled
            />
          </InputGrid>

          <InputGrid minWidth={12}>
            <TextInput
              value={state.email ?? ''}
              name="email"
              label="Email Address"
              onChange={changeProperty}
              disabled
            />
          </InputGrid>
        </>}

        <Grid container justifyContent='center' gap={3}>
          <Grid item sm={4}>
            <CustomButton
              EndIcon={Cancel}
              label="Cancel"
              onClick={onClose}
              disabled={loading || !isReadyToSubmit()}
            />
          </Grid>
          <Grid item sm={4}>
            <CustomButton
              EndIcon={Save}
              label="Save "
              loading={loading}
              onClick={handleSignUp}
              disabled={loading || !isReadyToSubmit()}
            />
          </Grid>
        </Grid>
      </Grid>
    </FormWrapper>
  );
};
